import type { GiftCard } from "~/models/GiftCard"
import type { ShopCart } from "~/models/ShopCart"

export const useShopCartStore = defineStore("shop_cart_store", {
    state: (): {
        shopCart: ShopCart
        loadingShopCart: boolean
        cartCount: number | null
        giftCards: GiftCard[]
    } => ({
        shopCart: {
            items: [],
            count: 0,
            subtotal: 0,
            subtotal_formatted: "",
            shipment_formatted: "",
            to_pay_formatted: "",
            tax_formatted: "",
            is_wholesaler: false,
        },
        loadingShopCart: false,
        cartCount: null,
        giftCards: [],
    }),
    getters: {
        // Basic state checks
        isEmpty: (state) => (state.cartCount ?? 0) === 0 && state.shopCart.items.length === 0,
        isLoading: (state) => state.loadingShopCart,
        hasItems: (state) => (state.cartCount ?? 0) > 0 || state.shopCart.items.length > 0,
       
        shouldShowEmptyState(): boolean {
            return this.isEmpty && !this.isLoading
        },
        shouldShowSkeleton(): boolean {
            return this.isLoading
        },
        shouldShowCartItems(): boolean {
            return this.hasItems && !this.isLoading
        }
    },
    actions: {
        async clearCart() {
            this.shopCart = {
                items: [],
                count: 0,
                subtotal: 0,
                subtotal_formatted: "",
                shipment_formatted: "",
                to_pay_formatted: "",
                tax_formatted: "",
                is_wholesaler: false,
            }
            this.cartCount = 0
            await this.updateShopCartData()
        },
        async getGiftCards() {
            const { get: fetchGiftCards } = useOFetchCustom("api/public/giftCards/list")

            await fetchGiftCards(undefined, {
                onSuccess: ({ data }) => {
                    this.giftCards = data
                },
                onError: () => {}
            })
        },
        async fetchShopCart(
            headers?: Record<string, string | number>,
            onlyFetch?: boolean
        ) {
            
            this.startLoading()
            try {
                if (!onlyFetch) {
                    await this.cartItemsCount()
                }

                
                if (onlyFetch || (this.cartCount && this.cartCount > 0)) {
                    await this.updateShopCartData(headers)
                } else {
                    this.stopLoading()
                }
            } catch (error) {
                console.error('Error fetching shop cart:', error)
                this.stopLoading()
            }
        },
        async updateShopCartData(headers?: Record<string, string | number>) {
            const { get: fetchCart } = useOFetchCustom("shop-cart-data", true)
            await fetchCart(undefined, {
                headers: headers,
                onSuccess: ({ data }) => {
                    this.shopCart = data
                    this.cartCount = data?.count ?? 0
                },
                onError: (e) => {
                    console.log(e)
                    this.stopLoading()
                },
                onFinish: () => {
                    this.stopLoading()
                }
            })
        },
        async cartItemsCount() {
            const { get: fetchCount } = useOFetchCustom("cart/count", true)
            this.startLoading()
            await fetchCount(undefined, {
                onSuccess: (data) => {
                    this.cartCount = data?.count ?? 0
                    this.shopCart.count = data?.count ?? 0
                    this.stopLoading()
                },
                onError: (error) => {
                    console.error(
                        "Error al obtener el conteo del carrito:",
                        error
                    )
                },
                onFinish: () => {
                    this.stopLoading()
                }
            })
        },
        async addToCart(body: any): Promise<any> {
            const { post } = useOFetchCustom("cart", true)

            return new Promise((resolve, reject) => {
                post(body, {
                    onSuccess: (res: any) => {
                        this.cartCount = res?.count ?? 0
                        this.shopCart = { ...this.shopCart, ...res?.summary, count: res?.count, subtotal_formatted: res?.summary?.subtotal_formatted }
                        resolve(res)
                    },
                    onError: (error: any) => {
                        reject(new Error(error?.message || 'Cart operation failed'))
                    }
                })
            })
        },
        removeToCart() {
            this.fetchShopCart(undefined, true)
        },
        startLoading() {
            this.loadingShopCart = true
        },
        stopLoading() {
            this.loadingShopCart = false
        }
    },
})
